import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Brain,
  CircleHelp,
  Crown,
  Ellipsis,
  Flag,
  Flame,
  Home,
  LibraryBig,
  LogOut,
  MessageSquareText,
  Moon,
  NotebookPen,
  Scissors,
  Settings,
  Sparkles,
  Sun,
  TrendingUp,
  Trophy,
  Upload,
} from 'lucide-react';
import { useMaterial3Theme } from '../../contexts/Material3ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useEntitlements } from '../../contexts/EntitlementsContext';
import analyticsService from '../../services/analytics';
import { getRouteCompatibility } from '../../config/routeCompatibility';
import BottomSheet from '../BottomSheet';
import { MD3MenuDivider, MD3MenuItem } from '../Material3';

const icon = (Icon) => <Icon size={22} strokeWidth={2} aria-hidden="true" />;

const MoreMenu = ({ isOpen, onClose, navigate, isDark, onToggleTheme, onLogout, isPremium }) => {
  const sections = [
    {
      label: 'Library tools',
      items: [
        { Icon: Upload, label: 'Upload Book', path: '/upload' },
        { Icon: Scissors, label: 'Clippings', path: '/clippings' },
        { Icon: LibraryBig, label: 'Collections', path: '/collections' },
      ],
    },
    {
      label: 'AI and discovery',
      items: [
        { Icon: Sparkles, label: 'Recommendations', path: '/recommendations' },
        ...(!isPremium ? [{ Icon: Crown, label: 'Upgrade to Pro', path: '/pricing', accent: true }] : []),
      ],
    },
    {
      label: 'Progress',
      items: [
        { Icon: Flame, label: 'Streak', path: '/streak' },
        { Icon: TrendingUp, label: 'Progress and Journey', path: '/progress' },
        { Icon: Flag, label: 'Goals and Achievements', path: '/goals' },
        { Icon: Trophy, label: 'Leaderboard', path: '/leaderboard' },
      ],
    },
    {
      label: 'System',
      items: [
        { Icon: Settings, label: 'Settings', path: '/settings' },
        { Icon: CircleHelp, label: 'Help and FAQ', path: '/help' },
        { Icon: MessageSquareText, label: 'Send Feedback', path: '/feedback' },
      ],
    },
  ];

  const handleNavigate = (path) => {
    const compatibility = getRouteCompatibility(path);
    analyticsService.trackEvent('navigation_selected', {
      category: 'core_funnel',
      source: 'mobile_more',
      destination: compatibility?.destination || 'utility',
      route_template: compatibility?.current || path,
    });
    onClose();
    navigate(path);
  };

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose} title="More options" height="large">
      <div role="menu" aria-label="More navigation options">
        <MD3MenuItem
          tabIndex={0}
          icon={icon(isDark ? Sun : Moon)}
          onClick={onToggleTheme}
          aria-label={`Switch to ${isDark ? 'light' : 'dark'} mode`}
        >
          {isDark ? 'Light Mode' : 'Dark Mode'}
        </MD3MenuItem>
        <MD3MenuDivider />

        {sections.map((section) => (
          <div key={section.label} role="group" aria-label={section.label}>
            <div className="md3-menu-group__label" aria-hidden="true">{section.label}</div>
            {section.items.map(({ Icon, label, path, accent }) => (
              <MD3MenuItem
                key={path}
                tabIndex={0}
                icon={icon(Icon)}
                className={accent ? 'md3-menu-item--accent' : ''}
                onClick={() => handleNavigate(path)}
              >
                {label}
              </MD3MenuItem>
            ))}
          </div>
        ))}

        <MD3MenuDivider />
        <MD3MenuItem
          tabIndex={0}
          icon={icon(LogOut)}
          className="md3-menu-item--destructive"
          onClick={() => {
            onClose();
            onLogout();
          }}
        >
          Logout
        </MD3MenuItem>
      </div>
    </BottomSheet>
  );
};

const MobileNavigation = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { actualTheme, toggleTheme } = useMaterial3Theme();
  const { logout } = useAuth();
  const { isPremium } = useEntitlements();
  const [isMobile, setIsMobile] = useState(false);
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);

  useEffect(() => {
    const media = window.matchMedia('(max-width: 1080px)');
    const update = () => setIsMobile(media.matches);
    update();
    media.addEventListener?.('change', update);
    return () => media.removeEventListener?.('change', update);
  }, []);

  const destinations = [
    { key: 'dashboard', Icon: Home, label: 'Home', path: '/dashboard' },
    { key: 'library', Icon: LibraryBig, label: 'Library', path: '/library' },
    { key: 'mentor', Icon: Brain, label: 'Mentor', path: '/mentor' },
    { key: 'notes', Icon: NotebookPen, label: 'Notes', path: '/notes' },
    { key: 'more', Icon: Ellipsis, label: 'More', path: null },
  ];

  const isActive = (path) => pathname === path || pathname.startsWith(`${path}/`);

  const handleDestination = (destination) => {
    if (!destination.path) {
      analyticsService.trackEvent('navigation_selected', {
        category: 'core_funnel',
        source: 'mobile_bar',
        destination: 'utility',
        route_template: '/more',
      });
      setIsMoreMenuOpen(true);
      return;
    }

    const compatibility = getRouteCompatibility(destination.path);
    analyticsService.trackEvent('navigation_selected', {
      category: 'core_funnel',
      source: 'mobile_bar',
      destination: compatibility?.destination || destination.key,
      route_template: compatibility?.current || destination.path,
    });
    navigate(destination.path);
  };

  if (!isMobile) return null;

  const isDark = actualTheme === 'dark';
  const containerStyle = {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    minHeight: 'calc(72px + env(safe-area-inset-bottom, 0px))',
    backgroundColor: 'var(--sq-color-surface-container)',
    borderTop: '1px solid var(--sq-color-outline-variant)',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-around',
    padding: '4px max(4px, env(safe-area-inset-right, 0px)) env(safe-area-inset-bottom, 0px) max(4px, env(safe-area-inset-left, 0px))',
    zIndex: 1200,
    boxShadow: 'var(--sq-elevation-2)',
  };
  const itemStyle = (active) => ({
    display: 'flex',
    flex: '1 1 0',
    minWidth: 0,
    minHeight: '64px',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '4px',
    padding: '6px 2px',
    border: 0,
    borderRadius: 'var(--sq-radius-large)',
    background: active ? 'var(--sq-color-secondary-container)' : 'transparent',
    color: active ? 'var(--sq-color-on-secondary-container)' : 'var(--sq-color-on-surface-variant)',
    font: 'inherit',
    cursor: 'pointer',
  });

  return (
    <>
      <nav style={containerStyle} aria-label="Primary">
        {destinations.map((destination) => {
          const active = destination.path ? isActive(destination.path) : isMoreMenuOpen;
          const Icon = destination.Icon;
          return (
            <button
              key={destination.key}
              type="button"
              onClick={() => handleDestination(destination)}
              style={itemStyle(active)}
              aria-current={active && destination.path ? 'page' : undefined}
              aria-expanded={destination.key === 'more' ? isMoreMenuOpen : undefined}
              aria-haspopup={destination.key === 'more' ? 'dialog' : undefined}
            >
              <Icon size={24} strokeWidth={active ? 2.5 : 2} aria-hidden="true" />
              <span style={{ fontSize: '0.75rem', fontWeight: 600 }}>{destination.label}</span>
            </button>
          );
        })}
      </nav>
      <MoreMenu
        isOpen={isMoreMenuOpen}
        onClose={() => setIsMoreMenuOpen(false)}
        navigate={navigate}
        isDark={isDark}
        onToggleTheme={toggleTheme}
        onLogout={async () => {
          await logout();
          navigate('/login', { replace: true });
        }}
        isPremium={isPremium}
      />
    </>
  );
};

export default MobileNavigation;
